import type { UIMessage } from "@ai-sdk/react";
import type { SerializedUIMessage } from "@/features/ads";
import type { SessionPillItem } from "../components/agent-chat/SessionPillBar";

export const DRAFT_SUB_AGENT_SESSION_ID = "__draft__";

export interface AgentSessionMeta {
  id: string;
  title: string | null;
  status: string;
  createdAt: string;
  /** Semantic category of assets this session produces, stamped on the
   *  Anthropic session as `metadata.asset_kind`. Drives the pill label
   *  (e.g. "Image Ads", "Portraits"). Null on legacy sessions. */
  assetKind: string | null;
}

/**
 * Serialize messages preserving text, thinking, and tool parts.
 * Tool parts are stored with their final state so they restore correctly.
 */
export function serializeSubAgentMessages(messages: UIMessage[]): SerializedUIMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role as "user" | "assistant",
    parts: (message.parts ?? [])
      .map((part) => {
        const record = part as Record<string, unknown>;
        if (record.type === "text" && typeof record.text === "string") {
          return { type: "text" as const, text: record.text };
        }
        if (record.type === "thinking" && typeof record.thinking === "string") {
          return { type: "thinking" as const, thinking: record.thinking };
        }
        if (record.type === "dynamic-tool") {
          return {
            type: "dynamic-tool" as const,
            toolName: record.toolName as string,
            toolCallId: record.toolCallId as string,
            input: record.input,
            output: record.output,
            state: record.state as string,
          };
        }
        return null;
      })
      .filter(Boolean) as SerializedUIMessage["parts"],
  }));
}

export function deserializeSubAgentMessages(cached: SerializedUIMessage[]): UIMessage[] {
  // Cast through unknown: our thinking/dynamic-tool parts don't exist in the
  // AI SDK's UIMessagePart union, but ChatMessageList handles them at runtime.
  return cached.map((message) => ({
    id: message.id,
    role: message.role,
    parts: message.parts as unknown as UIMessage["parts"],
  }));
}

export function buildSubAgentSessionPills({
  sessionList,
  liveSessionId,
  currentTimeIso = new Date().toISOString(),
}: {
  sessionList: AgentSessionMeta[];
  liveSessionId: string | null;
  currentTimeIso?: string;
}): SessionPillItem[] {
  const real: SessionPillItem[] = sessionList.map((session) => ({
    id: session.id,
    label: session.title ?? session.id.slice(-6),
    createdAt: session.createdAt,
    assetKind: session.assetKind,
  }));

  if (liveSessionId && !real.some((session) => session.id === liveSessionId)) {
    real.unshift({
      id: liveSessionId,
      label: liveSessionId.slice(-6),
      createdAt: currentTimeIso,
      assetKind: null,
    });
  }

  if (liveSessionId === null && real.length > 0) {
    return [
      {
        id: DRAFT_SUB_AGENT_SESSION_ID,
        label: "New",
        createdAt: currentTimeIso,
        assetKind: null,
        isDraft: true,
      },
      ...real,
    ];
  }

  return real;
}
